import React from 'react';
import Amplify, { Auth as AmplifyAuth } from 'aws-amplify';
import { AuthState, onAuthUIStateChange } from '@aws-amplify/ui-components';
import { AmplifyAuthenticator, AmplifySignIn } from '@aws-amplify/ui-react';
import { AMPLIFY_CONFIG } from '../../../../amplify-config';
import { createGenericContext } from '../../../../utils/create-generic-context';
import { Branch } from '../../../../packages/branch/branch';
import { CognitoUser } from './types';

Amplify.configure(AMPLIFY_CONFIG);

interface AuthProps {
  children: React.ReactNode | React.ReactNodeArray;
}

export async function getAuthToken() {
  try {
    const session = await AmplifyAuth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    return undefined;
  }
}

export async function getAuthTokenHeader() {
  const token = await getAuthToken();
  if (!token) {
    return {};
  }

  return { Authorization: token };
}

export function useAuth() {
  const [authState, setAuthState] = React.useState<AuthState>();
  const [user, setUser] = React.useState<CognitoUser | undefined>();

  React.useEffect(() => {
    onAuthUIStateChange((nextAuthState, authData) => {
      setAuthState(nextAuthState);
      setUser(authData as CognitoUser);
    });
  }, []);

  React.useEffect(() => {
    AmplifyAuth.currentAuthenticatedUser()
      .then((user) => {
        setAuthState(AuthState.SignedIn);
        setUser(user);
      })
      .catch(() => {
        setAuthState(AuthState.SignedOut);
        setUser(undefined);
      });
  }, []);

  return {
    authState,
    user,
  };
}

export const [useAuthContext, AuthContextProvider] = createGenericContext<{
  authState: AuthState | undefined;
  user: CognitoUser | undefined;
  isAuthenticated: boolean;
}>();

export const PrivateRoute = ({
  children,
}: {
  children: React.ReactNode | React.ReactNodeArray;
}) => {
  const { authState } = useAuthContext();

  return (
    <Branch
      test={authState === AuthState.SignedIn}
      left={<React.Fragment>{children}</React.Fragment>}
      right={(
        <AmplifyAuthenticator>
          <AmplifySignIn
            submitButtonText="Logga in"
            formFields={[
              {
                type: 'email',
                label: 'E-post',
                placeholder: 'Skriv in din mailadress',
                required: true,
              },
              {
                type: 'password',
                label: 'Lösenord',
                placeholder: 'Ange ditt lösenord',
                required: true,
              },
            ]}
            headerText="Logga in"
            hideSignUp
            slot="sign-in"
          />
        </AmplifyAuthenticator>
      )}
    />
  );
};

export const Auth = ({ children }: AuthProps) => {
  const { user, authState } = useAuth();

  const isAuthenticated = authState === AuthState.SignedIn;

  return (
    <AuthContextProvider value={{ authState, user, isAuthenticated }}>
      {children}
    </AuthContextProvider>
  );
};
